import type { Directive } from 'vue'

interface TooltipEl extends HTMLElement {
  _tooltipContent?: string | null | undefined
  _tooltipLarge?: boolean
  _tooltipTop?: boolean
  _tooltipTouch?: boolean
  _tooltipCleanup?: () => void
}

let tooltipEl: HTMLDivElement | null = null

function tooltipRoot(): HTMLElement {
  return document.fullscreenElement instanceof HTMLElement
    ? document.fullscreenElement
    : document.body
}

function ensureTooltipEl(): HTMLDivElement {
  if (tooltipEl) {
    const root = tooltipRoot()
    if (tooltipEl.parentElement !== root) root.appendChild(tooltipEl)
    return tooltipEl
  }
  const el = document.createElement('div')
  el.className = 'app-tooltip'
  el.style.cssText = `
    position: fixed;
    z-index: 9999;
    background: var(--color-raised);
    color: var(--color-text);
    border: 1px solid var(--color-border-strong);
    border-radius: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: pre-line;
    max-width: 20rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease-out;
  `
  tooltipRoot().appendChild(el)
  tooltipEl = el
  return el
}

function positionTooltip(target: HTMLElement, content: string, large: boolean, preferTop: boolean) {
  const el = ensureTooltipEl()
  el.textContent = content
  el.style.left = '0px'
  el.style.top = '0px'
  el.style.fontSize = large ? '1rem' : '0.75rem'
  el.style.background = large ? 'var(--color-surface)' : 'var(--color-raised)'
  el.style.borderColor = large ? 'var(--color-border-subtle)' : 'var(--color-border-strong)'

  // 一度表示してサイズ計測
  el.style.visibility = 'hidden'
  el.style.opacity = '1'
  const tipRect = el.getBoundingClientRect()
  const rect = target.getBoundingClientRect()

  let left = rect.left + rect.width / 2 - tipRect.width / 2
  left = Math.max(4, Math.min(left, window.innerWidth - tipRect.width - 4))
  let top: number
  if (preferTop) {
    top = rect.top - tipRect.height - 6
    if (top < 4) top = rect.bottom + 6
  } else {
    top = rect.bottom + 6
    if (top + tipRect.height > window.innerHeight - 4) {
      top = rect.top - tipRect.height - 6
    }
  }

  el.style.left = `${left}px`
  el.style.top = `${top}px`
  el.style.visibility = 'visible'
}

let activeTouchEl: HTMLElement | null = null
let docListener: ((e: PointerEvent) => void) | null = null

function addDocCloseListener() {
  if (docListener) return
  docListener = (e: PointerEvent) => {
    if (!activeTouchEl) return
    const target = e.target as Node | null
    if (target && activeTouchEl.contains(target)) return
    if (tooltipEl) tooltipEl.style.opacity = '0'
    activeTouchEl = null
    removeDocCloseListener()
  }
  document.addEventListener('pointerdown', docListener, true)
}

function removeDocCloseListener() {
  if (!docListener) return
  document.removeEventListener('pointerdown', docListener, true)
  docListener = null
}

function bind(el: TooltipEl) {
  const show = () => {
    const content = el._tooltipContent
    if (!content) return
    positionTooltip(el, String(content), !!el._tooltipLarge, !!el._tooltipTop)
    if (tooltipEl) tooltipEl.style.opacity = '1'
  }

  const hide = () => {
    if (tooltipEl) tooltipEl.style.opacity = '0'
  }

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') return
    // タッチ向けツールチップは .touch モディファイアでオプトイン
    if (!el._tooltipTouch) return
    // タッチ/ペン: タップでトグル表示（hover が効かないため）
    if (activeTouchEl === el) {
      hide()
      activeTouchEl = null
      removeDocCloseListener()
    } else {
      show()
      activeTouchEl = el
      addDocCloseListener()
    }
  }

  // pointerType=mouse のみ hover で表示する。
  // タッチ操作後にブラウザが mouseenter/pointerenter を合成するケースを避けるため、
  // pointerType が mouse でない場合は明示的に無視する。
  const onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return
    if (activeTouchEl && activeTouchEl !== el) return
    show()
  }
  const onPointerLeave = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return
    if (activeTouchEl === el) return
    hide()
  }

  el.addEventListener('pointerenter', onPointerEnter)
  el.addEventListener('pointerleave', onPointerLeave)
  el.addEventListener('pointerdown', onPointerDown)

  el._tooltipCleanup = () => {
    if (activeTouchEl === el) {
      activeTouchEl = null
      removeDocCloseListener()
    }
    hide()
    el.removeEventListener('pointerenter', onPointerEnter)
    el.removeEventListener('pointerleave', onPointerLeave)
    el.removeEventListener('pointerdown', onPointerDown)
  }
}

export const vTooltip: Directive<TooltipEl, string | null | undefined> = {
  mounted(el, binding) {
    el._tooltipContent = binding.value
    el._tooltipLarge = binding.modifiers.lg
    el._tooltipTop = binding.modifiers.top
    el._tooltipTouch = binding.modifiers.touch
    bind(el)
  },
  updated(el, binding) {
    el._tooltipContent = binding.value
    el._tooltipLarge = binding.modifiers.lg
    el._tooltipTop = binding.modifiers.top
    el._tooltipTouch = binding.modifiers.touch
  },
  unmounted(el) {
    el._tooltipCleanup?.()
    delete el._tooltipCleanup
    delete el._tooltipContent
    delete el._tooltipLarge
    delete el._tooltipTop
    delete el._tooltipTouch
  },
}
